import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { WorkoutItem, useCancel, useComplete, useReschedule, useCopy } from "../planApi";
import { fmtDateHuman, isoDate } from "../dateUtils";
import { getStatusVisuals, rpeColor, STATUS_I18N_KEY } from "../workoutStatus";
import { FEEL_OPTIONS as FEEL_ICONS, FeelIcon } from "./FeelIcon";
import { extractApiError } from "../lib/errors";





function fmtDuration(min: number | null, hShort: string, mShort: string) {
  if (min == null) return null;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return h ? `${h}${hShort} ${m}${mShort}` : `${m} ${mShort}`;
}

type Mode = "view" | "complete" | "reschedule" | "cancel" | "copy";

/**
 * variant:
 *   - "sheet" (default, legacy) — mobile bottom sheet anchored bottom-of-viewport
 *   - "drawer" — canon desktop right-side slide-in (Storica Desktop - Plan.dc.html section drawer)
 */
export type WorkoutActionVariant = "sheet" | "drawer";

export function WorkoutActionModal({
  wo,
  onClose,
  variant = "sheet",
}: {
  wo: WorkoutItem;
  onClose: () => void;
  variant?: WorkoutActionVariant;
}) {
  const { t } = useTranslation("workout");
  const { t: tc, i18n } = useTranslation("common");
  const disciplineLabel = wo.discipline
    ? tc(`sport.${wo.discipline}`, { defaultValue: wo.discipline })
    : null;
  const hShort = tc("hour_short");
  const mShort = tc("min_short");
  const kmShort = tc("km_short");
  const [mode, setMode] = useState<Mode>("view");
  const [rpe, setRpe] = useState<number | null>(null);
  const [feel, setFeel] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const [newDate, setNewDate] = useState<string>(wo.scheduled_date);
  const [reason, setReason] = useState<string>("");
  const [err, setErr] = useState<string | null>(null);

  const complete = useComplete();
  const reschedule = useReschedule();
  const copy = useCopy();
  const nav = useNavigate();
  const cancel = useCancel();
  const isPlanned = wo.status === "planned" || wo.status === "rescheduled";

  const onComplete = () => {
    setErr(null);
    complete.mutate(
      { id: wo.id, rpe: rpe ?? undefined, feel: feel || undefined, notes: notes || undefined },
      { onSuccess: onClose, onError: (e: unknown) => setErr(extractApiError(e, tc("error"))) },
    );
  };
  const onReschedule = () => {
    setErr(null);
    reschedule.mutate(
      { id: wo.id, new_date: newDate },
      { onSuccess: onClose, onError: (e: unknown) => setErr(extractApiError(e, tc("error"))) },
    );
  };
  const onCopy = () => {
    setErr(null);
    copy.mutate(
      { id: wo.id, new_date: newDate },
      { onSuccess: onClose, onError: (e: unknown) => setErr(extractApiError(e, tc("error"))) },
    );
  };
  const onCancel = () => {
    setErr(null);
    cancel.mutate(
      { id: wo.id, reason: reason || undefined },
      { onSuccess: onClose, onError: (e: unknown) => setErr(extractApiError(e, tc("error"))) },
    );
  };

  const busy = complete.isPending || reschedule.isPending || cancel.isPending || copy.isPending;

  const isDrawer = variant === "drawer";

  return (
    <div
      data-testid="workout-action-overlay"
      data-variant={variant}
      className={
        isDrawer
          ? "fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex justify-end"
          : "fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-end"
      }
      onClick={onClose}
    >
      <div
        data-testid="workout-action-panel"
        className={
          isDrawer
            ? "bg-bg-card h-full w-full max-w-[500px] p-6 space-y-3 overflow-y-auto shadow-drawer"
            : "bg-bg-surface w-full rounded-t-card p-4 space-y-3 max-h-[92vh] overflow-y-auto border-t border-bg-border"
        }
        style={
          isDrawer
            ? { boxShadow: "var(--shadow-drawer)" }
            : { paddingBottom: "calc(1rem + env(safe-area-inset-bottom))" }
        }
        onClick={(e) => e.stopPropagation()}
      >
        {/* drag handle — sheet only */}
        {!isDrawer && (
          <div className="w-10 h-1 rounded-full bg-bg-divider mx-auto -mt-1 mb-2" />
        )}

        <div className="flex justify-between items-start gap-3">
          <div className="min-w-0">
            <div className="label-caps">{fmtDateHuman(wo.scheduled_date, i18n?.language ?? "ru")} · {STATUS_I18N_KEY[wo.status] ? tc(STATUS_I18N_KEY[wo.status]) : getStatusVisuals(wo.status).label}</div>
            <div className="text-xl font-semibold text-fg-primary mt-1 truncate">
              {wo.title || disciplineLabel || t("modal_fallback_title")}
            </div>
          </div>
          <button onClick={onClose} aria-label={tc("close")} className="text-fg-muted hover:text-fg-primary p-1 -m-1 rounded shrink-0">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="text-sm text-fg-secondary flex flex-wrap gap-x-3 gap-y-1 num-display">
          {/* дисциплину не дублируем, когда она уже стоит титулом */}
          {disciplineLabel && !!wo.title && <span>{disciplineLabel}</span>}
          {wo.planned_duration_minutes != null && <span>{t("modal_plan_prefix")} {fmtDuration(wo.planned_duration_minutes, hShort, mShort)}</span>}
          {wo.planned_distance_km != null && <span>{t("modal_plan_prefix")} {wo.planned_distance_km} {kmShort}</span>}
        </div>

        {wo.description && mode === "view" && (
          <div className="text-sm whitespace-pre-line text-fg-primary border-t border-bg-divider pt-3">
            {wo.description}
          </div>
        )}

        {(wo.status === "completed" || wo.status === "done") && mode === "view" && (
          <div className="border-t border-bg-divider pt-3 space-y-2">
            <div className="label-caps">{t("modal_actual")}</div>
            <div className="text-sm text-fg-primary flex flex-wrap gap-x-3 gap-y-1 items-center num-display">
              {wo.actual_distance_km != null && <span>{wo.actual_distance_km} {kmShort}</span>}
              {wo.actual_duration_minutes != null && <span>{fmtDuration(wo.actual_duration_minutes, hShort, mShort)}</span>}
              {wo.rpe != null && (
                <span className={`px-2 py-0.5 rounded font-medium text-xs ${rpeColor(wo.rpe)}`}>RPE {wo.rpe}</span>
              )}
              {wo.feel && <FeelIcon feel={wo.feel} size={16} withLabel />}
            </div>
            {wo.athlete_notes && (
              <div className="text-sm text-fg-secondary mt-1 whitespace-pre-line">{wo.athlete_notes}</div>
            )}
          </div>
        )}

        {err && (
          <div className="text-sm text-bad bg-bad-subtle/40 border border-bad/30 rounded-pill px-3 py-2">
            {err}
          </div>
        )}

        {/* У ВЫПОЛНЕННОЙ главное действие — посмотреть разбор, а не
            скопировать. До этой правки страница разбора была из плана
            недостижима вовсе: модалка предлагала одно копирование, и вся
            история упиралась в тупик. */}
        {mode === "view" && !isPlanned && (
          <div className="flex gap-2 border-t border-bg-divider pt-3">
            <button
              onClick={() => nav(`/workout/${wo.id}`)}
              data-testid="modal-open-analysis"
              className="flex-1 py-2.5 rounded-pill bg-accent text-accent-on font-semibold text-sm"
            >
              {t("modal_analysis", { defaultValue: "Разбор" })}
            </button>
            <button
              onClick={() => setMode("copy")}
              className="flex-1 py-2.5 rounded-pill bg-bg-raised border border-bg-border text-sm"
            >
              {t("modal_copy")}
            </button>
          </div>
        )}
        {mode === "view" && isPlanned && (
          <div className="grid grid-cols-2 gap-2 pt-2">
            <button
              onClick={() => setMode("complete")}
              className="py-2.5 rounded-pill bg-accent text-accent-on font-semibold text-sm col-span-2"
            >
              {t("modal_complete")}
            </button>
            <button
              onClick={() => setMode("reschedule")}
              className="py-2.5 rounded-pill bg-bg-raised border border-bg-border text-fg-primary text-sm"
            >
              {t("modal_reschedule")}
            </button>
            <button
              onClick={() => setMode("copy")}
              className="py-2.5 rounded-pill bg-bg-raised border border-bg-border text-fg-primary text-sm"
            >
              {t("modal_copy")}
            </button>
            <button
              onClick={() => setMode("cancel")}
              className="py-2.5 rounded-pill bg-bg-raised border border-bg-border text-fg-primary text-sm col-span-2"
            >
              {t("modal_cant")}
            </button>
          </div>
        )}

        {/* Выход на день целиком. Из карточки одной сессии не видно ни того,
            что в этом дне есть ещё работа, ни утренних показателей — а
            судить о тренировке без них неполно. */}
        {mode === "view" && (
          <button
            type="button"
            onClick={() => nav(`/day/${wo.scheduled_date}`)}
            data-testid="modal-open-day"
            className="w-full text-center font-mono text-[10px] uppercase tracking-[0.16em] text-fg-muted hover:text-fg-primary pt-1"
          >
            {t("modal_open_day", { defaultValue: "Весь день" })}
          </button>
        )}

        {mode === "complete" && (
          <div className="space-y-3 border-t border-bg-divider pt-3">
            <div>
              <div className="label-caps mb-2">RPE 1–10</div>
              <div className="grid grid-cols-10 gap-1">
                {Array.from({ length: 10 }).map((_, i) => {
                  const v = i + 1;
                  const active = rpe === v;
                  return (
                    <button
                      key={v}
                      onClick={() => setRpe(v)}
                      className={`aspect-square rounded-md text-sm font-medium transition-colors ${
                        active
                          ? "bg-accent text-accent-on"
                          : "bg-bg-raised text-fg-secondary border border-bg-border hover:border-accent/40"
                      }`}
                    >
                      {v}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <div className="label-caps mb-2">{t("modal_feel")}</div>
              <div className="flex flex-wrap gap-2">
                {FEEL_ICONS.map((o) => {
                  const Icon = o.icon;
                  const on = feel === o.value;
                  return (
                    <button
                      key={o.value}
                      onClick={() => setFeel(o.value)}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-pill text-sm transition-colors ${
                        on
                          ? "bg-accent text-accent-on"
                          : "bg-bg-raised text-fg-secondary border border-bg-border hover:border-accent/40"
                      }`}
                    >
                      <Icon size={16} strokeWidth={2} className={on ? "" : o.cls} />
                      {t(o.i18nKey)}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <div className="label-caps mb-2">{t("modal_notes")}</div>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="w-full rounded-pill bg-bg-base border border-bg-border focus:border-accent focus:shadow-focus outline-none p-3 text-sm text-fg-primary placeholder:text-fg-dim transition-shadow"
                placeholder={t("modal_notes_placeholder")}
              />
            </div>
            <div className="flex gap-2">
              <button onClick={() => setMode("view")} className="flex-1 py-2.5 rounded-pill bg-bg-raised border border-bg-border text-sm">
                {tc("back")}
              </button>
              <button
                onClick={onComplete}
                disabled={busy}
                className="flex-1 py-2.5 rounded-pill bg-accent text-accent-on font-semibold disabled:opacity-50 text-sm"
              >
                {busy ? "..." : tc("save")}
              </button>
            </div>
          </div>
        )}

        {mode === "reschedule" && (
          <div className="space-y-3 border-t border-bg-divider pt-3">
            <div>
              <div className="label-caps mb-2">{t("modal_new_date")}</div>
              <input
                type="date"
                value={newDate}
                min={isoDate(new Date())}
                onChange={(e) => setNewDate(e.target.value)}
                className="w-full rounded-pill bg-bg-base border border-bg-border focus:border-accent focus:shadow-focus outline-none p-3 text-sm text-fg-primary transition-shadow"
              />
            </div>
            <div className="flex gap-2">
              <button onClick={() => setMode("view")} className="flex-1 py-2.5 rounded-pill bg-bg-raised border border-bg-border text-sm">
                {tc("back")}
              </button>
              <button
                onClick={onReschedule}
                disabled={busy || !newDate}
                className="flex-1 py-2.5 rounded-pill bg-accent text-accent-on font-semibold disabled:opacity-50 text-sm"
              >
                {busy ? "..." : t("modal_reschedule")}
              </button>
            </div>
          </div>
        )}
        {mode === "copy" && (
          <div className="space-y-3 border-t border-bg-divider pt-3">
            <div>
              <div className="label-caps mb-2">{t("modal_copy_date")}</div>
              <input
                type="date"
                value={newDate}
                min={isoDate(new Date())}
                onChange={(e) => setNewDate(e.target.value)}
                className="w-full rounded-pill bg-bg-base border border-bg-border focus:border-accent focus:shadow-focus outline-none p-3 text-sm text-fg-primary transition-shadow"
              />
              <div className="text-[11px] text-fg-muted mt-2">{t("modal_copy_hint")}</div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setMode("view")} className="flex-1 py-2.5 rounded-pill bg-bg-raised border border-bg-border text-sm">
                {tc("back")}
              </button>
              <button
                onClick={onCopy}
                disabled={busy || !newDate}
                className="flex-1 py-2.5 rounded-pill bg-accent text-accent-on font-semibold disabled:opacity-50 text-sm"
              >
                {busy ? "..." : t("modal_copy")}
              </button>
            </div>
          </div>
        )}

        {mode === "cancel" && (
          <div className="space-y-3 border-t border-bg-divider pt-3">
            <div>
              <div className="label-caps mb-2">{t("modal_reason")}</div>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                className="w-full rounded-pill bg-bg-base border border-bg-border focus:border-accent focus:shadow-focus outline-none p-3 text-sm text-fg-primary placeholder:text-fg-dim transition-shadow"
                placeholder={t("modal_reason_placeholder")}
              />
            </div>
            <div className="flex gap-2">
              <button onClick={() => setMode("view")} className="flex-1 py-2.5 rounded-pill bg-bg-raised border border-bg-border text-sm">
                {tc("back")}
              </button>
              <button
                onClick={onCancel}
                disabled={busy}
                className="flex-1 py-2.5 rounded-pill bg-rose-500/15 border border-rose-500/30 text-rose-300 font-medium disabled:opacity-50 text-sm"
              >
                {busy ? "..." : tc("cancel")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
